/** The Events tab (WP25; `decideSlots.eventsTab`) and the live-surprise takeover the participant frame draws over every page. */
import { useMemo, useRef, type ComponentType } from "react";
import type { TeamState } from "@bsim/contracts";
import type { RouteCall } from "../api/types";
import type { PreviewLimits } from "../decisions/previewRows";
import { forecastGateOpen } from "../decisions/forecast/forecast";
import type { EventsTabProps } from "../decisions/slotTypes";
import { GxTakeover } from "../theme/Takeover";
import { formatCountdown } from "../shell/RoundClock";
import { useNow } from "../shell/useNow";
import { LIVE, positionIn } from "./deck";
import { EventCard } from "./EventCard";
import { RoutineReviewLine } from "./Deferral";
import "./events.css";

/** The Bank › Risk › ALM page for this cohort, where a card\u2019s rate position can be read. */
const almHrefOf = (team: TeamState): string => `/c/${team.cohort.id}/financials?tab=risk.alm`;

/** Answers go one after another: a second card submitted while the first is still saving waits for it, so neither draft write overtakes the other (the lost answer of round 22). */
export function useSerialised<A extends readonly unknown[], R>(call: (...args: A) => Promise<R>): (...args: A) => Promise<R> {
  const tail = useRef<Promise<unknown>>(Promise.resolve());
  return useMemo(() => (...args: A) => {
    const run = tail.current.then(() => call(...args), () => call(...args));
    tail.current = run.catch(() => undefined);
    return run;
  }, [call]);
}

export const EventsTab: ComponentType<EventsTabProps> = ({ team, respondToEvent: respond, limits, risks, planEve200, refreshFailed }) => {
  const respondToEvent = useSerialised(respond) as RouteCall<"respondToEvent">;
  const forecastCommitted = forecastGateOpen(team);
  const planned = team.pendingEvents.filter((e) => e.mode === "planned" && LIVE.has(e.status));
  return (
    <div className="gx-events" data-testid="events.tab">
      <RoutineReviewLine titles={(team.droppedReviews ?? []).map((r) => r.title)} />
      {planned.map((e) => <EventCard key={e.eventInstanceId} event={e} teamId={team.team.id} forecastCommitted={forecastCommitted} refreshFailed={refreshFailed} respondToEvent={respondToEvent} delayed={e.deferred === true} chip="Answer before you sign" kind="planned" position={positionIn(planned, e)} limits={limits} risks={risks} planEve200={planEve200} solo={team.cohort.mode === "solo"} almHref={almHrefOf(team)} />)}
    </div>
  );
};

/** Screens §P6 Live surprise: after close, a full-width sheet with the card and the only countdown (display only), wherever the team is. */
export function SurpriseTakeover({ team, respondToEvent, limits }: { readonly team: TeamState; readonly respondToEvent: RouteCall<"respondToEvent">; readonly limits?: PreviewLimits | undefined }) {
  const now = useNow();
  const surprise = team.pendingEvents.find((e) => e.mode === "surprise" && LIVE.has(e.status));
  // A round whose result is out has no live surprise left to answer: the sheet is not drawn over the results (the root of the sheet that sat on top of Results).
  if (surprise === undefined || team.round?.state === "resolved" || team.round?.state === "released") return null;
  const chip = surprise.responseCountdownEndsAt === null ? "Live surprise" : `Live surprise · ${formatCountdown(Date.parse(surprise.responseCountdownEndsAt) - now)}`;
  return (
    <GxTakeover label="Live surprise" testId="events.takeover">
      <h1 className="gx-takeover-lead">
        Submitted. The year is running. Your bank is paused at month {surprise.timing.phase === "midRound" ? surprise.timing.month : 0} for a surprise.
      </h1>
      <EventCard key={surprise.eventInstanceId} event={surprise} teamId={team.team.id} forecastCommitted={forecastGateOpen(team)} respondToEvent={respondToEvent} chip={chip} kind="paused" solo={team.cohort.mode === "solo"} limits={limits} almHref={almHrefOf(team)} />
      <p className="gx-takeover-note">Display only. Nothing happens when the countdown reaches zero.</p>
    </GxTakeover>
  );
}
